import React, {useRef, useState} from 'react';
import {Image, PanResponder, Platform, StyleSheet, View} from 'react-native';
import type {GestureResponderEvent, ImageProps, ViewStyle} from 'react-native';
import {useBlockAncestorEdgeBack} from './ChatEdgeBack';
import {constrainPhoto, initialPhotoTransform, loadedPhotoSize, movePhoto} from '../media/photoZoom';
import type {PhotoPoint, PhotoSize} from '../media/photoZoom';

type Props = {uri: string; imageTestID: string; accessibilityLabel: string; onLoad?: ImageProps['onLoad']; onError?: ImageProps['onError']};

// Reset on source changes; never retain a previous member's view/gesture state.
export default function ZoomablePhoto(props: Props) {return <Photo key={props.uri} {...props} />;}
function Photo({uri, imageTestID: testID, accessibilityLabel, onLoad, onError}: Props) {
  const [transform, setTransform] = useState(initialPhotoTransform);
  const state = useRef(initialPhotoTransform);
  const viewport = useRef<PhotoSize>({width: 0, height: 0});
  const image = useRef<PhotoSize>({width: 0, height: 0});
  const imageView = useRef<Image & {querySelector?: (selector: string) => {naturalWidth: number; naturalHeight: number} | null}>(null);
  const ready = useRef(false);
  const previous = useRef<PhotoPoint[]>([]);
  useBlockAncestorEdgeBack(transform.scale > 1);
  const update = (next: typeof transform) => {state.current = next; setTransform(next);};
  const reset = () => {previous.current = []; update(initialPhotoTransform);};
  const points = (event: GestureResponderEvent) => event.nativeEvent.touches.map(touch => ({x: touch.locationX, y: touch.locationY}));
  // Reserve the first finger too: native Modal otherwise owns the initial
  // responder, so the second finger may never negotiate a pinch with this view.
  // A single finger at 1x is a bounded no-op, not navigation or dismissal.
  const wants = (event: GestureResponderEvent) => ready.current && event.nativeEvent.touches.length > 0;
  const pan = useRef(PanResponder.create({
    onStartShouldSetPanResponderCapture: wants,
    onStartShouldSetPanResponder: wants,
    onMoveShouldSetPanResponder: wants,
    onPanResponderGrant: event => {previous.current = points(event);},
    onPanResponderMove: event => {
      const next = points(event);
      update(movePhoto(state.current, previous.current, next, viewport.current, image.current));
      previous.current = next;
    },
    onPanResponderRelease: () => {previous.current = [];},
    onPanResponderTerminate: () => {previous.current = [];},
    onPanResponderTerminationRequest: () => false,
  })).current;
  return <View testID={`${testID}-viewport`} style={[styles.viewport, Platform.OS === 'web' && webGestureStyle]} {...pan.panHandlers}
    onLayout={event => {
      const {width, height} = event.nativeEvent.layout;
      if (width !== viewport.current.width || height !== viewport.current.height) {viewport.current = {width, height}; reset();}
    }} accessible accessibilityRole="adjustable" accessibilityLabel={accessibilityLabel}
    accessibilityValue={{min: 100, max: 400, now: Math.round(transform.scale * 100), text: `${Math.round(transform.scale * 100)}%`}}
    aria-valuemin={100} aria-valuemax={400} aria-valuenow={Math.round(transform.scale * 100)} aria-valuetext={`${Math.round(transform.scale * 100)}%`}
    accessibilityActions={[{name: 'increment'}, {name: 'decrement'}]}
    onAccessibilityAction={event => {
      if (!ready.current || !['increment', 'decrement'].includes(event.nativeEvent.actionName)) return;
      update(constrainPhoto({...state.current, scale: state.current.scale + (event.nativeEvent.actionName === 'increment' ? 0.5 : -0.5)}, viewport.current, image.current));
    }}>
    <View pointerEvents="none" style={styles.image}>
    <Image ref={imageView} testID={testID} source={{uri}} resizeMode="contain" accessible={false}
      style={[styles.image, {transform: [{translateX: transform.x}, {translateY: transform.y}, {scale: transform.scale}]}]}
      onLoad={event => {
        // RN Web may deliver an asynchronous load event with no target. Read
        // only this rendered image, never query another photo or fetch again.
        const size = loadedPhotoSize(event, Platform.OS === 'web' ? imageView.current?.querySelector?.('img') : null);
        image.current = size ?? {width: 0, height: 0}; ready.current = size !== null;
        if (!size) reset();
        onLoad?.(event);
      }} onError={event => {ready.current = false; reset(); onError?.(event);}} />
    </View>
  </View>;
}
const styles = StyleSheet.create({viewport: {flex: 1, width: '100%', minHeight: 0, overflow: 'hidden'}, image: {width: '100%', height: '100%'}});
// Restrict browser gesture ownership to the photo surface, not the whole page.
const webGestureStyle: ViewStyle & {touchAction: 'none'} = {touchAction: 'none'};
